import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';

const CompletionPage = ({ responses, verifiedToken }) => {
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to get response value by question ID
  const getResponseValue = (questionId) => {
    for (const pageId in responses) {
      const pageResponses = responses[pageId];
      if (pageResponses && pageResponses[questionId] !== undefined) {
        return pageResponses[questionId];
      }
    }
    return null;
  };

  const transformResponsesToAPIFormat = (responses, personalInfo, verifiedToken) => {
    const now = new Date().toISOString();

    // Helper function to filter out null/undefined/empty values
    // Note: Only filters optional fields - required fields are always included
    const filterEmptyValues = (obj, requiredFields = []) => {
      const filtered = {};
      for (const [key, value] of Object.entries(obj)) {
        // Always include required fields, even if they're empty/null
        if (requiredFields.includes(key)) {
          // For scale questions (1-6), ensure minimum value of 1 if present
          if (typeof value === 'number' && value === 0 && key.match(/^(financial_statements|profitability|valuation|customer_base|customer_contracts|sales_growth|brand_value|marketing_strategies|market_position|customer_relationships|growth_strategy|revenue_streams|management_capability|leadership_roles|succession_planning|employee_turnover|business_processes|it_systems|operations_continuity|technology_systems|proprietary_tech|operational_processes|scalability|supplier_contracts|operating_expenses|risk_management|business_resilience|legal_contracts)$/)) {
            filtered[key] = null; // Let validation catch missing scale values
          } else {
            filtered[key] = value;
          }
        } else if (value !== null && value !== undefined && value !== '' && value !== 0) {
          filtered[key] = value;
        }
      }
      return filtered;
    };

    // Helper function to convert boolean values to yes/no strings
    const convertBooleanToYesNo = (value) => {
      if (typeof value === 'boolean') {
        return value ? 'yes' : 'no';
      }
      return value;
    };

    // Helper function to convert frontend dropdown values to API format
    const convertDropdownValues = (value) => {
      const mappings = {
        // Exit timeline mappings
        '0-1_years': '0-1 year',
        '1_2_years': '1-2 years',
        '2_5_years': '2-5 years',
        '5_plus_years': '5+ years',
        
        // Industry mappings
        'retail': 'Retail',
        'restaurants': 'Restaurants',
        'construction': 'Construction',
        'manufacturing': 'Manufacturing',
        'professional_services': 'Professional Services',
        'healthcare': 'Healthcare (Non-Medical)',
        'ecommerce': 'E-commerce',
        'wholesale_distribution': 'Wholesale/Distribution',
        'auto_repair': 'Auto Repair',
        'beauty_personal_care': 'Beauty/Personal Care',
        'it_services': 'IT Services',
        'other': 'Other',
        
        // Business readiness mappings (keeping as is, but ready for future API changes)
        'business_would_fall_apart_without_me': 'business would fall apart without me',
        'business_would_struggle_some_but_remain_functioning': 'business would struggle some but remain functioning',
        'business_would_run_well_independently_with_strong_management': 'business would run well/independently with strong management'
      };
      
      return mappings[value] || value;
    };

    // Transform business goals and financials (Page 1)
    const businessGoalsAndFinancialsRaw = {
      company_name: getResponseValue('q1_1'),
      company_industry: convertDropdownValues(getResponseValue('q1_2')),
      number_of_employees: getResponseValue('q1_3') ? parseInt(getResponseValue('q1_3')) : null,
      current_business_value: getResponseValue('q1_7') ? parseFloat(getResponseValue('q1_7')) : null,
      target_sale_price: getResponseValue('q1_8') ? parseFloat(getResponseValue('q1_8')) : null,
      last_year_revenue: getResponseValue('q1_9') ? parseFloat(getResponseValue('q1_9')) : null,
      last_year_profit: getResponseValue('q1_10') ? parseFloat(getResponseValue('q1_10')) : null,
      current_year_estimated_revenue: getResponseValue('q1_11') ? parseFloat(getResponseValue('q1_11')) : null,
      current_year_estimated_profit: getResponseValue('q1_12') ? parseFloat(getResponseValue('q1_12')) : null,
      planned_exit_timeline: convertDropdownValues(getResponseValue('q1_4')),
      would_accept_offer: convertBooleanToYesNo(getResponseValue('q1_5')),
      business_readiness: convertDropdownValues(getResponseValue('q1_6'))
    };

    // Transform business performance and transferability (Pages 2 & 3)
    const businessPerformanceAndTransferabilityRaw = {
      financial_statements: getResponseValue('q2_1') ? parseInt(getResponseValue('q2_1')) : null,
      profitability: getResponseValue('q2_2') ? parseInt(getResponseValue('q2_2')) : null,
      valuation: getResponseValue('q2_3') ? parseInt(getResponseValue('q2_3')) : null,
      customer_base: getResponseValue('q2_4') ? parseInt(getResponseValue('q2_4')) : null,
      customer_contracts: getResponseValue('q2_5') ? parseInt(getResponseValue('q2_5')) : null,
      sales_growth: getResponseValue('q2_6') ? parseInt(getResponseValue('q2_6')) : null,
      brand_value: getResponseValue('q2_7') ? parseInt(getResponseValue('q2_7')) : null,
      marketing_strategies: getResponseValue('q2_8') ? parseInt(getResponseValue('q2_8')) : null,
      market_position: getResponseValue('q2_9') ? parseInt(getResponseValue('q2_9')) : null,
      customer_relationships: getResponseValue('q2_10') ? parseInt(getResponseValue('q2_10')) : null,
      growth_strategy: getResponseValue('q2_11') ? parseInt(getResponseValue('q2_11')) : null,
      revenue_streams: getResponseValue('q2_12') ? parseInt(getResponseValue('q2_12')) : null,
      management_capability: getResponseValue('q2_13') ? parseInt(getResponseValue('q2_13')) : null,
      leadership_roles: getResponseValue('q2_14') ? parseInt(getResponseValue('q2_14')) : null,
      succession_planning: getResponseValue('q2_15') ? parseInt(getResponseValue('q2_15')) : null,
      employee_turnover: getResponseValue('q3_1') ? parseInt(getResponseValue('q3_1')) : null,
      business_processes: getResponseValue('q3_2') ? parseInt(getResponseValue('q3_2')) : null,
      it_systems: getResponseValue('q3_3') ? parseInt(getResponseValue('q3_3')) : null,
      operations_continuity: getResponseValue('q3_4') ? parseInt(getResponseValue('q3_4')) : null,
      technology_systems: getResponseValue('q3_5') ? parseInt(getResponseValue('q3_5')) : null,
      proprietary_tech: getResponseValue('q3_6') ? parseInt(getResponseValue('q3_6')) : null,
      operational_processes: getResponseValue('q3_7') ? parseInt(getResponseValue('q3_7')) : null,
      scalability: getResponseValue('q3_8') ? parseInt(getResponseValue('q3_8')) : null,
      supplier_contracts: getResponseValue('q3_9') ? parseInt(getResponseValue('q3_9')) : null,
      operating_expenses: getResponseValue('q3_10') ? parseInt(getResponseValue('q3_10')) : null,
      risk_management: getResponseValue('q3_11') ? parseInt(getResponseValue('q3_11')) : null,
      business_resilience: getResponseValue('q3_12') ? parseInt(getResponseValue('q3_12')) : null,
      legal_contracts: getResponseValue('q3_13') ? parseInt(getResponseValue('q3_13')) : null
    };

    // Transform personal readiness (Page 4)
    const personalReadinessForBusinessOwnersRaw = {
      personal_identity: getResponseValue('q4_1') ? parseInt(getResponseValue('q4_1')) : null,
      financial_plan: getResponseValue('q4_2') ? parseInt(getResponseValue('q4_2')) : null,
      physical_health: getResponseValue('q4_3') ? parseInt(getResponseValue('q4_3')) : null,
      energy_level: getResponseValue('q4_4') ? parseInt(getResponseValue('q4_4')) : null,
      estate_plan: getResponseValue('q4_5') ? parseInt(getResponseValue('q4_5')) : null,
      legal_protections: getResponseValue('q4_6') ? parseInt(getResponseValue('q4_6')) : null,
      future_vision: getResponseValue('q4_7') ? parseInt(getResponseValue('q4_7')) : null,
      family_communication: getResponseValue('q4_8') ? parseInt(getResponseValue('q4_8')) : null,
      professional_advisors: getResponseValue('q4_9') ? parseInt(getResponseValue('q4_9')) : null,
      process_confidence: getResponseValue('q4_10') ? parseInt(getResponseValue('q4_10')) : null
    };

    // Define required fields based on backend schema
    const businessGoalsRequiredFields = [
      'company_name', 'company_industry', 'number_of_employees', 
      'current_business_value', 'target_sale_price', 'last_year_revenue',
      'last_year_profit', 'current_year_estimated_revenue', 'current_year_estimated_profit',
      'planned_exit_timeline', 'would_accept_offer', 'business_readiness'
    ];
    
    const businessPerformanceRequiredFields = [
      'financial_statements', 'profitability', 'valuation', 'customer_base',
      'customer_contracts', 'sales_growth', 'brand_value', 'marketing_strategies',
      'market_position', 'customer_relationships', 'growth_strategy', 'revenue_streams',
      'management_capability', 'leadership_roles', 'succession_planning',
      'employee_turnover', 'business_processes', 'it_systems', 'operations_continuity',
      'technology_systems', 'proprietary_tech', 'operational_processes', 'scalability',
      'supplier_contracts', 'operating_expenses', 'risk_management', 'business_resilience',
      'legal_contracts'
    ];

    // Filter out empty values (but keep required fields)
    const businessGoalsAndFinancials = filterEmptyValues(businessGoalsAndFinancialsRaw, businessGoalsRequiredFields);
    const businessPerformanceAndTransferability = filterEmptyValues(businessPerformanceAndTransferabilityRaw, businessPerformanceRequiredFields);
    const personalReadinessForBusinessOwners = filterEmptyValues(personalReadinessForBusinessOwnersRaw, []); // No required fields

    // Build assessment data object
    const assessmentData = {
      business_goals_and_financials: businessGoalsAndFinancials,
      business_performance_and_transferability: businessPerformanceAndTransferability
    };

    // Only include personal readiness if it has any values
    if (Object.keys(personalReadinessForBusinessOwners).length > 0) {
      assessmentData.personal_readiness_for_business_owners = personalReadinessForBusinessOwners;
    }

    return {
      metadata: {
        date_sent: now,
        source: "web",
        version: "1.0"
      },
      first_name: personalInfo.firstName,
      last_name: personalInfo.lastName,
      email: personalInfo.email,
      phone_number: personalInfo.phoneNumber,
      assessment_data: assessmentData,
      ...(verifiedToken && { token: verifiedToken })
    };
  };

  const validateRequiredFields = () => {
    const businessGoalsRequiredFields = [
      'q1_1', 'q1_2', 'q1_3', 'q1_4', 'q1_5', 'q1_6', 
      'q1_7', 'q1_8', 'q1_9', 'q1_10', 'q1_11', 'q1_12'
    ];
    
    const businessPerformanceRequiredFields = [
      'q2_1', 'q2_2', 'q2_3', 'q2_4', 'q2_5', 'q2_6', 'q2_7', 'q2_8', 'q2_9', 'q2_10', 'q2_11', 'q2_12', 'q2_13', 'q2_14', 'q2_15',
      'q3_1', 'q3_2', 'q3_3', 'q3_4', 'q3_5', 'q3_6', 'q3_7', 'q3_8', 'q3_9', 'q3_10', 'q3_11', 'q3_12', 'q3_13'
    ];

    const missingFields = [];
    
    // Check business goals and financials
    for (const fieldId of businessGoalsRequiredFields) {
      const value = getResponseValue(fieldId);
      if (value === null || value === undefined || value === '') {
        missingFields.push(fieldId);
      }
    }
    
    // Check business performance and transferability
    for (const fieldId of businessPerformanceRequiredFields) {
      const value = getResponseValue(fieldId);
      if (value === null || value === undefined || value === '' || value === 0) {
        missingFields.push(fieldId);
      }
    }
    
    return missingFields;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    // Validate required fields
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      setSubmitError(`Please complete all required questions before submitting. Missing ${missingFields.length} required responses.`);
      setIsSubmitting(false);
      return;
    }

    try {
      const apiData = transformResponsesToAPIFormat(responses, personalInfo, verifiedToken);
      
      const response = await axios.post(
        API_CONFIG.buildURL(API_CONFIG.ENDPOINTS.DATA.SUBMIT),
        apiData,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      console.log('Successfully submitted assessment data:', response.data);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting assessment data:', error);
      setSubmitError(error.response?.data?.message || 'Failed to submit assessment data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validatePhoneNumber = (phone) => {
    // Backend pattern: ^\\+?[1-9]\\d{1,14}$
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone) && phone.length >= 10;
  };

  const isFormValid = personalInfo.firstName && personalInfo.lastName && 
                     personalInfo.email && personalInfo.phoneNumber &&
                     validatePhoneNumber(personalInfo.phoneNumber);

  if (submitted) {
    return (
      <div className="completion-page">
        <div className="completion-header">
          <h2>Thank You for Completing the Assessment!</h2>
          <p>Your responses have been recorded successfully.</p>
          <p>You will be receiving your detailed report via email shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="completion-page">
      <div className="completion-header">
        <h2>Complete Your Assessment</h2>
        <p>Please provide your contact information to receive your detailed report.</p>
      </div>
      
      <form onSubmit={handleSubmit} className="personal-info-form">
        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={personalInfo.firstName}
            onChange={handleInputChange}
            required
            placeholder="Enter your first name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={personalInfo.lastName}
            onChange={handleInputChange}
            required
            placeholder="Enter your last name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={personalInfo.email}
            onChange={handleInputChange}
            required
            placeholder="Enter your email address"
          />
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number *</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={personalInfo.phoneNumber}
            onChange={handleInputChange}
            required
            placeholder="Enter your phone number (e.g., +1234567890)"
          />
          {personalInfo.phoneNumber && !validatePhoneNumber(personalInfo.phoneNumber) && (
            <small style={{ color: '#f44336', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
              Please enter a valid phone number (10+ digits, optionally starting with +)
            </small>
          )}
        </div>

        {submitError && (
          <div className="error-message">
            {submitError}
          </div>
        )}

        <button 
          type="submit" 
          className="submit-btn"
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Assessment & Get Report'}
        </button>
      </form>
    </div>
  );
};

export default CompletionPage; 